import { SDK } from "@axl-dev/school-sdk";
import { Modal, Spin, theme } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFileProvider, useLoc } from "@app/shared/lib/hooks";
import { formatDate } from "@app/shared/lib/date";
import { generateBtnHoverCss, parseBtnHoverProps, BtnHoverProps, sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";
import { deviceVarCss } from "@app/widgets/shared/deviceCss";

type SourceMode = "all" | "specificCerts" | "specificCourses";

type StudentCertItem = {
  id: string;
  number: string;
  createdDate: string;
  cert: { id: string; name: string } | null;
  course: { id: string; name: string } | null;
  image: { id: string; cloudKey: string } | null;
};

export type CertificateListWidgetProps = BtnHoverProps & {
  sdk: SDK;
  sourceMode: SourceMode;
  layout: "grid" | "list";
  cardGap: string;
  cardWidth: string;
  cardBorderRadius: string;
  cardBgColor: string;
  cardBorderColor: string;
  cardPadding: string;
  cardPaddingTablet: string;
  cardPaddingMobile: string;
  imageHeight: string;
  imageBorderRadius: string;
  imageObjectFit: "cover" | "contain";
  showCertName: boolean;
  certNameColor: string;
  certNameFontWeight: string;
  certNameMargin: string;
  showCourseName: boolean;
  courseNameColor: string;
  courseNameFontSize: string;
  showDate: boolean;
  dateColor: string;
  dateFontSize: string;
  showNumber: boolean;
  numberColor: string;
  numberFontSize: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  btnBgColor: string;
  btnTextColor: string;
  btnBorderRadius: string;
  btnFontSize: string;
  btnPaddingV: string;
  btnPaddingH: string;
  btnWidth: string;
  btnAlign: "left" | "center" | "right";
  btnText: string;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
  certIds?: string[];
  courseIds?: string[];
  title?: string;
  prefetchedCerts?: StudentCertItem[];
};

export const parseCertificateListProps = (container: HTMLElement): Omit<CertificateListWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  const jsonAttr = (name: string): string[] => {
    const v = container.getAttribute(name);
    if (!v) return [];
    try { return JSON.parse(v); } catch (e) { console.error("[CertificateListWidget] parse error:", e); return []; }
  };

  return {
    sourceMode: (attr("data-source-mode", "all") as SourceMode),
    layout: attr("data-layout", "grid") as "grid" | "list",
    cardGap: attr("data-card-gap", "20px"),
    cardWidth: attr("data-card-width", "280px"),
    cardBorderRadius: attr("data-card-border-radius", "8px"),
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    cardPadding: attr("data-card-padding", "10px"),
    cardPaddingTablet: attr("data-card-padding-tablet", ""),
    cardPaddingMobile: attr("data-card-padding-mobile", ""),
    imageHeight: attr("data-image-height", "200px"),
    imageBorderRadius: attr("data-image-border-radius", "6px"),
    imageObjectFit: attr("data-image-object-fit", "contain") as "cover" | "contain",
    showCertName: attr("data-show-cert-name", "true") === "true",
    certNameColor: attr("data-cert-name-color", "#333333"),
    certNameFontWeight: attr("data-cert-name-font-weight", "500"),
    certNameMargin: attr("data-cert-name-margin", "8px 0 4px 0"),
    showCourseName: attr("data-show-course-name", "true") === "true",
    courseNameColor: attr("data-course-name-color", "#888888"),
    courseNameFontSize: attr("data-course-name-font-size", "12px"),
    showDate: attr("data-show-date", "true") === "true",
    dateColor: attr("data-date-color", "#999999"),
    dateFontSize: attr("data-date-font-size", "12px"),
    showNumber: attr("data-show-number", "false") === "true",
    numberColor: attr("data-number-color", "#999999"),
    numberFontSize: attr("data-number-font-size", "12px"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    btnBgColor: attr("data-btn-bg-color", "#1890ff"),
    btnTextColor: attr("data-btn-text-color", "#ffffff"),
    btnBorderRadius: attr("data-btn-border-radius", "5px"),
    btnFontSize: attr("data-btn-font-size", "13px"),
    btnPaddingV: attr("data-btn-padding-v", "6px"),
    btnPaddingH: attr("data-btn-padding-h", "16px"),
    btnWidth: attr("data-btn-width", "100%"),
    btnAlign: (attr("data-btn-align", "center") as "left" | "center" | "right"),
    btnText: attr("data-btn-text"),
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    certIds: jsonAttr("data-cert-ids"),
    courseIds: jsonAttr("data-course-ids"),
    title: attr("data-title") || undefined,
    ...parseBtnHoverProps(container),
  };
};

export const CertificateListWidget: React.FC<CertificateListWidgetProps> = (props) => {
  const {
    sdk, sourceMode, layout,
    cardGap, cardWidth, cardBorderRadius, cardPadding,
    imageHeight, imageBorderRadius, imageObjectFit,
    showCertName, certNameColor, certNameFontWeight, certNameMargin,
    showCourseName, courseNameColor, courseNameFontSize,
    showDate, dateColor, dateFontSize,
    showNumber, numberColor, numberFontSize,
    btnBorderRadius, btnFontSize, btnPaddingV, btnPaddingH, btnWidth, btnAlign, btnText,
    emptyText, emptyTextColor, emptyTextFontSize,
    certIds, courseIds, title,
    prefetchedCerts,
  } = props;

  const { token } = theme.useToken();
  const cardBg = props.cardBgColor || token.colorBgElevated;
  const cardBorder = props.cardBorderColor || token.colorBorder;
  const btnBg = props.btnBgColor || token.colorPrimary;
  const btnTextCol = props.btnTextColor || token.colorTextLightSolid;

  const loc = useLoc();
  const fileProvider = useFileProvider();
  const [certs, setCerts] = useState<StudentCertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    if (prefetchedCerts) {
      setCerts(prefetchedCerts);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const fields = "{ id, number, createdDate, cert { id, name }, course { id, name }, image { id, cloudKey } }";
        const result = await sdk.api.get<{ items: StudentCertItem[] }>("/student/cert", fields);
        if (!cancelled && result.success && result.body?.items) {
          setCerts(result.body.items);
        }
      } catch (e) {
        console.error("[CertificateListWidget] fetch error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sdk, prefetchedCerts]);

  const filteredCerts = useMemo(() => {
    if (sourceMode === "specificCerts" && certIds && certIds.length > 0) {
      return certs.filter(c => c.cert && certIds.includes(c.cert.id));
    }
    if (sourceMode === "specificCourses" && courseIds && courseIds.length > 0) {
      return certs.filter(c => c.course && courseIds.includes(c.course.id));
    }
    return certs;
  }, [certs, sourceMode, certIds, courseIds]);

  const getImageUrl = useCallback((cert: StudentCertItem) => {
    if (cert.image?.cloudKey) {
      return fileProvider.getUrlByKey(cert.image.cloudKey);
    }
    return undefined;
  }, [fileProvider]);

  const handleDownload = useCallback(async (cert: StudentCertItem) => {
    setDownloading(cert.id);
    try {
      const result = await sdk.api.file.download(cert.image!.id);
      if (result.success && result.body) {
        const link = document.createElement("a");
        link.href = result.body;
        link.download = `${cert.cert?.name || "certificate"}-${cert.number}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (e) {
      console.error("[CertificateListWidget] fetch error:", e);
    } finally {
      setDownloading(null);
    }
  }, [sdk]);


  const scopeId = useMemo(() => `ez-cert-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, "button, a", props), [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);
  const isGrid = layout === "grid";
  const gridCss = useMemo(() => !isGrid ? "" : `
    .${scopeId} .cert-grid { display: flex; flex-wrap: wrap; align-items: stretch; gap: ${sc(cardGap || "20px")}; }
  `, [scopeId, isGrid, cardGap]);

  const deviceCss = useMemo(() => deviceVarCss(`.${scopeId}`, [
    { name: "cert-pad", base: cardPadding || "10px", tablet: props.cardPaddingTablet, mobile: props.cardPaddingMobile },
  ]), [scopeId, cardPadding, props.cardPaddingTablet, props.cardPaddingMobile]);
  const themeCss = useMemo(() =>
    `.${scopeId} .ez-certificate-list-card{border:1px solid ${sc(cardBorder)};border-radius:${sc(cardBorderRadius || "8px")};background-color:${sc(cardBg)};}`
    + `.${scopeId} .ez-certificate-list-btn{border-radius:${sc(btnBorderRadius || "5px")};font-size:${sc(btnFontSize || "13px")};padding:${sc(btnPaddingV || "6px")} ${sc(btnPaddingH || "16px")};background-color:${sc(btnBg)};color:${sc(btnTextCol)};}`,
    [scopeId, cardBorder, cardBorderRadius, cardBg, btnBorderRadius, btnFontSize, btnPaddingV, btnPaddingH, btnBg, btnTextCol]);

  const downloadLabel = btnText || loc.word("CertificateList.download", { default: "Download" });
  const emptyLabel = emptyText || loc.word("CertificateList.empty", { default: "No certificates yet" });

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
      <Spin />
    </div>;
  }

  return (
    <div className={scopeId}>
      <style>{hoverCss}{gridCss}{deviceCss}{themeCss}</style>
      {title && (
        <div style={{
          fontSize: props.titleFontSize || "20px",
          fontWeight: (props.titleFontWeight as any) || 600,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: (props.titleTextAlign as any) || "left",
          marginBottom: "12px",
        }}>{title}</div>
      )}

      {filteredCerts.length === 0 ? (
        <div style={{
          textAlign: "center",
          padding: "40px 20px",
          color: emptyTextColor || token.colorTextSecondary,
          fontSize: emptyTextFontSize || "14px",
        }}>
          {emptyLabel}
        </div>
      ) : (
        <div
          className={isGrid ? "cert-grid" : ""}
          style={isGrid ? {} : { display: "flex", flexDirection: "column", gap: cardGap || "20px" }}
        >
          {filteredCerts.map((cert) => {
            const imgUrl = getImageUrl(cert);
            return (
              <div key={cert.id} className="ez-cab-card ez-certificate-list-card" style={{
                width: isGrid ? (cardWidth || "280px") : "100%",
                maxWidth: "100%",
                overflow: "hidden",
                padding: "var(--cert-pad, 10px)",
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
              }}>
                {imgUrl ? (
                  <div
                    style={{
                      width: "100%",
                      height: imageHeight || "200px",
                      borderRadius: imageBorderRadius || "6px",
                      overflow: "hidden",
                      cursor: "pointer",
                      backgroundColor: token.colorFillSecondary,
                      flexShrink: 0,
                    }}
                    onClick={() => setPreviewUrl(imgUrl)}
                  >
                    <img
                      src={imgUrl}
                      alt={cert.cert?.name || ""}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: imageObjectFit || "contain",
                        display: "block",
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    width: "100%",
                    height: imageHeight || "200px",
                    borderRadius: imageBorderRadius || "6px",
                    backgroundColor: token.colorFillSecondary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: token.colorTextQuaternary,
                    fontSize: "32px",
                    flexShrink: 0,
                  }}>
                    &#x1F3C6;
                  </div>
                )}

                <div style={{ flex: "1 1 auto" }}>
                  {showCertName && cert.cert && (
                    <div className="cert-name ez-cab-title ez-certificate-list-title" style={{
                      color: certNameColor || token.colorText,
                      fontWeight: (certNameFontWeight as any) || 500,
                      margin: certNameMargin || "8px 0 4px 0",
                    }}>{cert.cert.name}</div>
                  )}

                  {showCourseName && cert.course && (
                    <div style={{
                      color: courseNameColor || token.colorTextSecondary,
                      fontSize: courseNameFontSize || "12px",
                      marginBottom: "2px",
                    }}>{cert.course.name}</div>
                  )}
                </div>

                <div style={{ marginTop: "auto", paddingTop: "8px" }}>
                  {showNumber && cert.number && (
                    <div style={{
                      color: numberColor || token.colorTextSecondary,
                      fontSize: numberFontSize || "12px",
                      marginBottom: "2px",
                    }}>#{cert.number}</div>
                  )}

                  {showDate && cert.createdDate && (
                    <div style={{
                      color: dateColor || token.colorTextSecondary,
                      fontSize: dateFontSize || "12px",
                      marginBottom: "6px",
                    }}>{formatDate(cert.createdDate)}</div>
                  )}

                  {cert.image && (
                    <div style={{
                      display: "flex",
                      justifyContent: btnAlign === "right" ? "flex-end" : btnAlign === "left" ? "flex-start" : "center",
                    }}>
                      <button
                        className="ez-cab-btn ez-certificate-list-btn"
                        onClick={() => handleDownload(cert)}
                        disabled={downloading === cert.id}
                        style={{
                          display: "block",
                          width: btnWidth || "100%",
                          textAlign: "center",
                          border: "none",
                          cursor: downloading === cert.id ? "wait" : "pointer",
                          opacity: downloading === cert.id ? 0.7 : 1,
                        }}
                      >
                        {downloading === cert.id
                          ? (loc.word("CertificateList.downloading", { default: "Downloading..." }))
                          : downloadLabel}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!previewUrl}
        footer={null}
        onCancel={() => setPreviewUrl(null)}
        width="90vw"
        style={{ top: 20, maxWidth: 1200 }}
        styles={{ body: { padding: 0, display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#000" } }}
      >
        {previewUrl && (
          <img
            src={previewUrl}
            alt="Certificate"
            style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain" }}
          />
        )}
      </Modal>
    </div>
  );
};
